import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, Button, Table, Modal, Form, Input, message, Space, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { getAllStates, getQuestionsByState, addQuestion, updateQuestion, deleteQuestion } from "./laundryAuditApi";

const { Option } = Select;
const { confirm } = Modal;

// Flatter than Cleaning Audit's Questions screen — this app's question model
// has no Where/Why comment bank, just state-scoped question_text/question_info
// (confirmed by reading MajesticERP-LaundryAuditAPI/code/controllers/AuditQuestionsController.js directly).

const QuestionFormModal = ({ visible, editing, stateId, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        question_text: editing?.question_text || "",
        question_info: editing?.question_info || "",
      });
    }
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = { state_id: stateId, question_text: values.question_text, question_info: values.question_info };
      const { error, result } = editing
        ? await updateQuestion({ ...payload, id: editing.id })
        : await addQuestion(payload);
      if (error) message.error(result?.message || "Failed to save question.");
      else {
        message.success(editing ? "Question updated." : "Question added.");
        onSaved();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Edit Question" : "Add Question"} visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="question_text" label="Question Text" rules={[{ required: true, message: "Please enter the question text." }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="question_info" label="Help Text" rules={[{ required: true, message: "Please enter the help text." }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Questions = () => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [formModal, setFormModal] = useState({ visible: false, editing: null });

  useEffect(() => {
    (async () => {
      const { error, result } = await getAllStates();
      setStates(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const loadQuestions = useCallback(async () => {
    if (!stateId) {
      message.error("Please select a state first.");
      return;
    }
    setLoading(true);
    setLoaded(true);
    const { error, result } = await getQuestionsByState(stateId);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [stateId]);

  const handleDelete = useCallback((row) => {
    confirm({
      title: "Delete question",
      content: `Delete "${row.question_text}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteQuestion(row.id);
        if (error) message.error(result?.message || "Failed to delete question.");
        else { message.success("Question deleted."); loadQuestions(); }
      },
    });
  }, [loadQuestions]);

  const columns = [
    { title: "Question Text", dataIndex: "question_text", key: "question_text" },
    { title: "Help Text", dataIndex: "question_info", key: "question_info" },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setFormModal({ visible: true, editing: row })} type="primary" aria-label="Edit" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 220 }}
              value={stateId}
              onChange={setStateId}
            >
              {states.map((s) => <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>)}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={loadQuestions}>Load Questions</Button>
          {loaded && (
            <Button icon={<PlusOutlined />} onClick={() => setFormModal({ visible: true, editing: null })}>Add Question</Button>
          )}
        </div>
      </Card>

      {loaded && (
        <Card title="Questions">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No questions for this state yet." }}
          />
        </Card>
      )}

      <QuestionFormModal
        visible={formModal.visible}
        editing={formModal.editing}
        stateId={stateId}
        onClose={() => setFormModal({ visible: false, editing: null })}
        onSaved={loadQuestions}
      />
    </div>
  );
};

export default memo(Questions);
